import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  Select,
  StatCard,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { ChannelMeter, LicenseStatusBadge } from '@/components/License';
import { formatDay, formatMoney } from '@/lib/format';
import {
  BILLING_MODE_HINTS,
  BILLING_MODE_LABELS,
  daysLeftText,
  type BillingMode,
  type LicenseInvoice,
  type LicenseView,
} from '@/lib/license';

interface AdminLicense extends LicenseView {
  invoices: LicenseInvoice[];
  payments: Array<{
    id: string;
    amount: number;
    method: string | null;
    reference: string | null;
    paid_at: string | null;
    invoice_number: string | null;
  }>;
}

const INVOICE_TONES: Record<string, 'green' | 'amber' | 'gray' | 'red'> = {
  PAID: 'green',
  OPEN: 'amber',
  DRAFT: 'gray',
  VOID: 'gray',
  UNCOLLECTIBLE: 'red',
};

export function InvoiceStatus({ invoice }: { invoice: Pick<LicenseInvoice, 'status' | 'due_at'> }) {
  const overdue = invoice.status === 'OPEN' && invoice.due_at && new Date(invoice.due_at) < new Date();
  return (
    <Badge tone={overdue ? 'red' : INVOICE_TONES[invoice.status] ?? 'gray'} dot>
      {overdue ? 'Overdue' : invoice.status.charAt(0) + invoice.status.slice(1).toLowerCase()}
    </Badge>
  );
}

interface FormState {
  ai_bot_channels: string;
  soundboard_channels: string;
  ai_bot_channel_price: string;
  soundboard_channel_price: string;
  license_expires_at: string;
  license_grace_days: string;
  billing_mode: BillingMode;
}

function toForm(view: LicenseView): FormState {
  const ai = view.engines.find((e) => e.kind === 'AI');
  const sb = view.engines.find((e) => e.kind === 'SOUNDBOARD');
  return {
    ai_bot_channels: String(ai?.channels ?? 0),
    soundboard_channels: String(sb?.channels ?? 0),
    ai_bot_channel_price: String(ai?.monthly_price ?? 0),
    soundboard_channel_price: String(sb?.monthly_price ?? 0),
    license_expires_at: view.expires_at ? view.expires_at.slice(0, 10) : '',
    license_grace_days: String(view.grace_days ?? 0),
    billing_mode: view.billing_mode,
  };
}

/**
 * The License & billing tab of a client: what they bought, until when, and
 * what they owe. Everything here is the platform's to set.
 */
export function ClientLicense({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const key = ['client-license', clientId];
  const { data, isPending, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => api.get<AdminLicense>(`/admin/clients/${clientId}/license`),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [message, setMessage] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);
  const [paying, setPaying] = useState<LicenseInvoice | null>(null);
  const [voiding, setVoiding] = useState<LicenseInvoice | null>(null);
  const [lifting, setLifting] = useState(false);

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['licensing'] });
    queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<LicenseView>(`/admin/clients/${clientId}/license`, body),
    onSuccess: () => {
      setMessage({ tone: 'good', text: 'Licence saved. It applies to the next call.' });
      refresh();
    },
    onError: (err) => setMessage({ tone: 'bad', text: errorMessage(err) }),
  });

  const renew = useMutation({
    mutationFn: () => api.post<LicenseInvoice>(`/admin/clients/${clientId}/license/invoices`),
    onSuccess: (invoice) => {
      setMessage({ tone: 'good', text: `Invoice ${invoice.number} raised for ${formatMoney(invoice.total, invoice.currency)}.` });
      refresh();
    },
    onError: (err) => setMessage({ tone: 'bad', text: errorMessage(err) }),
  });

  const voidMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/invoices/${id}/void`),
    onSuccess: () => {
      setVoiding(null);
      refresh();
    },
    onError: (err) => setMessage({ tone: 'bad', text: errorMessage(err) }),
  });

  if (isPending || !form) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const set = (field: keyof FormState) => (value: string) =>
    setForm((current) => (current ? { ...current, [field]: value } : current));

  const monthly =
    Number(form.ai_bot_channels || 0) * Number(form.ai_bot_channel_price || 0) +
    Number(form.soundboard_channels || 0) * Number(form.soundboard_channel_price || 0);

  const submit = () => {
    setMessage(null);
    save.mutate({
      ai_bot_channels: Number(form.ai_bot_channels || 0),
      soundboard_channels: Number(form.soundboard_channels || 0),
      ai_bot_channel_price: form.ai_bot_channel_price || '0',
      soundboard_channel_price: form.soundboard_channel_price || '0',
      license_expires_at: form.license_expires_at || null,
      license_grace_days: Number(form.license_grace_days || 0),
      billing_mode: form.billing_mode,
      license_enforced: true,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Licence" value={<LicenseStatusBadge status={data.status} />} hint={data.enforced ? daysLeftText(data) : 'Limits not applied'} />
        <StatCard
          label="Expires"
          value={data.expires_at ? formatDay(data.expires_at) : '—'}
          hint={data.grace_days ? `+${data.grace_days} day grace` : 'No grace'}
          tone={data.status === 'expired' ? 'bad' : data.status === 'expiring' || data.status === 'grace' ? 'warn' : 'default'}
        />
        <StatCard label="Monthly value" value={formatMoney(data.monthly_total, data.currency)} hint={BILLING_MODE_LABELS[data.billing_mode]} />
        <StatCard
          label="Outstanding"
          value={formatMoney(
            data.invoices.filter((i) => i.status === 'OPEN' || i.status === 'DRAFT').reduce((sum, i) => sum + i.balance, 0),
            data.currency,
          )}
        />
      </div>

      {data.enforced && (
        <Card title="Channels in use now">
          <div className="grid gap-5 sm:grid-cols-2">
            {data.engines.map((engine) => (
              <ChannelMeter key={engine.kind} engine={engine} />
            ))}
          </div>
        </Card>
      )}

      <Card
        title="Licence"
        description="One channel is one simultaneous call. AI Bot and Soundboard channels are separate pools."
        footer={
          <>
            {message && (
              <span className={message.tone === 'good' ? 'mr-auto text-emerald-600 dark:text-emerald-400' : 'mr-auto text-red-600 dark:text-red-400'}>
                {message.text}
              </span>
            )}
            {data.enforced && (
              <Button variant="ghost" size="sm" onClick={() => setLifting(true)}>
                Remove limits
              </Button>
            )}
            <Button size="sm" onClick={submit} loading={save.isPending}>
              {data.enforced ? 'Save licence' : 'Apply licence'}
            </Button>
          </>
        }
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <fieldset className="space-y-3">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-500">AI Bot</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Channels">
                <Input type="number" min={0} value={form.ai_bot_channels} onChange={(e) => set('ai_bot_channels')(e.target.value)} />
              </Field>
              <Field label={`Price / channel / month (${data.currency})`}>
                <Input type="number" min={0} step="0.01" value={form.ai_bot_channel_price} onChange={(e) => set('ai_bot_channel_price')(e.target.value)} />
              </Field>
            </div>
          </fieldset>
          <fieldset className="space-y-3">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-500">Soundboard</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Channels">
                <Input type="number" min={0} value={form.soundboard_channels} onChange={(e) => set('soundboard_channels')(e.target.value)} />
              </Field>
              <Field label={`Price / channel / month (${data.currency})`}>
                <Input type="number" min={0} step="0.01" value={form.soundboard_channel_price} onChange={(e) => set('soundboard_channel_price')(e.target.value)} />
              </Field>
            </div>
          </fieldset>
          <Field label="Licence ends" hint="Calls are refused after this date (plus any grace). Paying a renewal invoice moves it on.">
            <Input type="date" value={form.license_expires_at} onChange={(e) => set('license_expires_at')(e.target.value)} />
          </Field>
          <Field label="Grace period" hint="Only if you allow it — three days at most.">
            <Select value={form.license_grace_days} onChange={(e) => set('license_grace_days')(e.target.value)}>
              <option value="0">None — stop at expiry</option>
              <option value="1">1 day</option>
              <option value="2">2 days</option>
              <option value="3">3 days</option>
            </Select>
          </Field>
          <Field label="Billing" hint={BILLING_MODE_HINTS[form.billing_mode]}>
            <Select value={form.billing_mode} onChange={(e) => set('billing_mode')(e.target.value as BillingMode)}>
              {(Object.keys(BILLING_MODE_LABELS) as BillingMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {BILLING_MODE_LABELS[mode]}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end">
            <p className="text-sm text-ink-600 dark:text-ink-300">
              Monthly licence value{' '}
              <span className="font-semibold tabular-nums text-ink-900 dark:text-ink-50">{formatMoney(monthly, data.currency)}</span>
            </p>
          </div>
        </div>
      </Card>

      <Card
        title="Invoices"
        padded={false}
        actions={
          <Button size="sm" variant="secondary" onClick={() => renew.mutate()} loading={renew.isPending} disabled={!data.enforced}>
            Raise renewal invoice
          </Button>
        }
      >
        {data.invoices.length === 0 ? (
          <EmptyState title="No invoices yet" description="Raise the first renewal invoice once the licence is set. They are also raised automatically a week before expiry." />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>Invoice</TH>
                  <TH>Type</TH>
                  <TH>Period</TH>
                  <TH>Status</TH>
                  <TH numeric>Total</TH>
                  <TH numeric>Balance</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {data.invoices.map((invoice) => (
                  <TR key={invoice.id}>
                    <TD className="font-mono text-xs">{invoice.number}</TD>
                    <TD>{invoice.kind === 'LICENSE' ? 'Licence' : 'Usage'}</TD>
                    <TD className="whitespace-nowrap text-xs">
                      {formatDay(invoice.period_start)} – {formatDay(invoice.period_end)}
                    </TD>
                    <TD>
                      <InvoiceStatus invoice={invoice} />
                    </TD>
                    <TD numeric>{formatMoney(invoice.total, invoice.currency)}</TD>
                    <TD numeric className="font-medium">{formatMoney(invoice.balance, invoice.currency)}</TD>
                    <TD className="whitespace-nowrap text-right">
                      {(invoice.status === 'OPEN' || invoice.status === 'DRAFT') && (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" onClick={() => setPaying(invoice)}>
                            Record payment
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setVoiding(invoice)}>
                            Void
                          </Button>
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {data.payments.length > 0 && (
        <Card title="Payments received" padded={false}>
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>Invoice</TH>
                  <TH>Method</TH>
                  <TH>Reference</TH>
                  <TH numeric>Amount</TH>
                </TR>
              </THead>
              <TBody>
                {data.payments.map((payment) => (
                  <TR key={payment.id}>
                    <TD>{formatDay(payment.paid_at)}</TD>
                    <TD className="font-mono text-xs">{payment.invoice_number ?? '—'}</TD>
                    <TD>{payment.method ?? '—'}</TD>
                    <TD className="text-xs">{payment.reference ?? '—'}</TD>
                    <TD numeric className="font-medium">{formatMoney(payment.amount, data.currency)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <PaymentModal invoice={paying} onClose={() => setPaying(null)} onDone={refresh} />

      <ConfirmDialog
        open={voiding !== null}
        title={`Void ${voiding?.number ?? ''}?`}
        body="The invoice stays on record as void and can no longer be paid. A paid invoice cannot be voided."
        confirmLabel="Void invoice"
        onConfirm={() => voiding && voidMutation.mutate(voiding.id)}
        onCancel={() => setVoiding(null)}
        busy={voidMutation.isPending}
      />

      <ConfirmDialog
        open={lifting}
        title="Remove licence limits?"
        body="Channel limits and expiry stop applying to this client: calls are no longer refused for channels or for an expired licence. The figures are kept and come back if you apply the licence again."
        confirmLabel="Remove limits"
        onConfirm={() => {
          setLifting(false);
          save.mutate({ license_enforced: false });
        }}
        onCancel={() => setLifting(false)}
      />
    </div>
  );
}

/** Money received against one invoice. Shared by the client tab and the Invoices page. */
export function PaymentModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: LicenseInvoice | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Bank transfer');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoice) {
      setAmount(String(invoice.balance ?? invoice.total));
      setReference('');
      // The operator's own today, not UTC's: money received this evening in
      // Karachi was received today.
      setPaidAt(new Date().toLocaleDateString('en-CA'));
      setError(null);
    }
  }, [invoice]);

  const pay = useMutation({
    mutationFn: () =>
      api.post(`/admin/invoices/${invoice?.id}/payments`, {
        amount,
        method: method || null,
        reference: reference || null,
        paid_at: paidAt ? `${paidAt}T12:00:00Z` : null,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={invoice !== null}
      onClose={onClose}
      title={`Record payment — ${invoice?.number ?? ''}`}
      description={
        invoice?.kind === 'LICENSE'
          ? 'Paid in full, this extends the licence to the end of the invoice period.'
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => pay.mutate()} loading={pay.isPending} disabled={!amount || Number(amount) <= 0}>
            Record payment
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`Amount (${invoice?.currency ?? 'USD'})`} hint={invoice ? `Balance ${formatMoney(invoice.balance, invoice.currency)}` : undefined}>
          <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Received on">
          <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        </Field>
        <Field label="Method">
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            {['Bank transfer', 'Card', 'Cash', 'Cheque', 'Other'].map((m) => (
              <option key={m}>{m}</option>
            ))}
          </Select>
        </Field>
        <Field label="Reference" hint="Bank reference, receipt number…">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} />
        </Field>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </Modal>
  );
}
